import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ChevronLeftIcon, ChevronRightIcon, PauseIcon, PlayIcon } from 'lucide-react';
interface ScreensaverProps {
  isActive: boolean;
  onDismiss: () => void;
}
const ambientImages = [{
  url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=2560&h=1440&fit=crop&q=90',
  caption: 'Swiss Alps, Switzerland',
  credit: 'Photo by Samuel Ferrara'
}, {
  url: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=2560&h=1440&fit=crop&q=90',
  caption: 'Misty Forest at Dawn',
  credit: 'Photo by Luca Bravo'
}, {
  url: 'https://images.unsplash.com/photo-1501854140801-50d01698950b?w=2560&h=1440&fit=crop&q=90',
  caption: 'Himalayan Peaks, India',
  credit: 'Photo by Kalen Emsley'
}, {
  url: 'https://images.unsplash.com/photo-1518020382113-a7e8fc38eac9?w=2560&h=1440&fit=crop&q=90',
  caption: 'Serene Lake Reflection',
  credit: 'Photo by Ales Krivec'
}, {
  url: 'https://images.unsplash.com/photo-1439853949212-36089c8d8f8a?w=2560&h=1440&fit=crop&q=90',
  caption: 'Golden Hour, Sahara',
  credit: 'Photo by Sergey Pesterev'
}, {
  url: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=2560&h=1440&fit=crop&q=90',
  caption: 'Maldives Horizon',
  credit: 'Photo by Sean Oulashin'
}, {
  url: 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=2560&h=1440&fit=crop&q=90',
  caption: 'Norwegian Fjords',
  credit: 'Photo by Tobias Tullius'
}, {
  url: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=2560&h=1440&fit=crop&q=90',
  caption: 'Dolomites, Italy',
  credit: 'Photo by Kalen Emsley'
}];
export function Screensaver({
  isActive,
  onDismiss
}: ScreensaverProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isPaused, setIsPaused] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [controlsTimeout, setControlsTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  // Clock
  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  // Auto-advance images
  useEffect(() => {
    if (!isActive || isPaused) return;
    const t = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % ambientImages.length);
    }, 8000);
    return () => clearInterval(t);
  }, [isActive, isPaused]);
  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeout) clearTimeout(controlsTimeout);
    const t = setTimeout(() => setShowControls(false), 3000);
    setControlsTimeout(t);
  };
  const prev = () => setCurrentIndex(i => (i - 1 + ambientImages.length) % ambientImages.length);
  const next = () => setCurrentIndex(i => (i + 1) % ambientImages.length);
  const formatTime = (d: Date) => d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
  const formatDate = (d: Date) => d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
  return <AnimatePresence data-id="element-512">
      {isActive && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 1.2
    }} className="fixed inset-0 z-50 overflow-hidden cursor-none" onMouseMove={handleMouseMove} onClick={onDismiss} data-id="element-513">
          {/* Images with crossfade */}
          <AnimatePresence mode="crossfade" data-id="element-514">
            <motion.div key={currentIndex} initial={{
          opacity: 0,
          scale: 1.04
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 1.5,
          ease: 'easeInOut'
        }} className="absolute inset-0" data-id="element-515">
              <img src={ambientImages[currentIndex].url} alt="" className="w-full h-full object-cover" data-id="element-516" />
              {/* Gradient overlays */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" data-id="element-517" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/20 via-transparent to-black/20" data-id="element-518" />
            </motion.div>
          </AnimatePresence>

          {/* Clock — center */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none" data-id="element-519">
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} className="text-center" data-id="element-520">
              <div className="text-[120px] font-thin text-white leading-none tracking-tight" style={{
            textShadow: '0 4px 40px rgba(0,0,0,0.5)'
          }} data-id="element-521">
                {formatTime(currentTime)}
              </div>
              <div className="text-2xl text-white/70 mt-4 font-light tracking-widest uppercase" style={{
            fontFamily: "'Playfair Display', serif",
            letterSpacing: '0.3em'
          }} data-id="element-522">
                {formatDate(currentTime)}
              </div>
            </motion.div>
          </div>

          {/* Bottom info bar */}
          <div className="absolute bottom-0 left-0 right-0 px-12 py-8 flex items-end justify-between pointer-events-none" data-id="element-523">
            <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.8
        }} data-id="element-524">
              <p className="text-white/90 text-lg font-light" style={{
            fontFamily: "'Playfair Display', serif"
          }} data-id="element-525">
                {ambientImages[currentIndex].caption}
              </p>
              <p className="text-white/40 text-sm mt-1" data-id="element-526">
                {ambientImages[currentIndex].credit}
              </p>
            </motion.div>

            {/* Qubix branding */}
            <div className="text-right" data-id="element-527">
              <p className="text-[#C9A84C] opacity-60 text-sm tracking-[0.3em] font-light" data-id="element-528">
                QUBIX
              </p>
              <p className="text-white/30 text-xs mt-1" data-id="element-529">
                Intelligent Executive Environments
              </p>
            </div>
          </div>

          {/* Image dots */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 pointer-events-none" data-id="element-530">
            {ambientImages.map((_, i) => <div key={i} className={`rounded-full transition-all duration-500 ${i === currentIndex ? 'w-6 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/30'}`} data-id="element-531" />)}
          </div>

          {/* Controls overlay (show on hover) */}
          <AnimatePresence data-id="element-532">
            {showControls && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 pointer-events-none" onClick={e => e.stopPropagation()} data-id="element-533">
                {/* Prev/Next */}
                <button onClick={e => {
            e.stopPropagation();
            prev();
          }} className="pointer-events-auto absolute left-8 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/50 transition-colors cursor-pointer" data-id="element-534">
                  <ChevronLeftIcon className="w-7 h-7" data-id="element-535" />
                </button>
                <button onClick={e => {
            e.stopPropagation();
            next();
          }} className="pointer-events-auto absolute right-8 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/50 transition-colors cursor-pointer" data-id="element-536">
                  <ChevronRightIcon className="w-7 h-7" data-id="element-537" />
                </button>

                {/* Top controls */}
                <div className="pointer-events-auto absolute top-8 right-8 flex items-center gap-3" data-id="element-538">
                  <button onClick={e => {
              e.stopPropagation();
              setIsPaused(!isPaused);
            }} className="w-12 h-12 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/50 transition-colors cursor-pointer" data-id="element-539">
                    {isPaused ? <PlayIcon className="w-5 h-5" data-id="element-540" /> : <PauseIcon className="w-5 h-5" data-id="element-541" />}
                  </button>
                  <button onClick={e => {
              e.stopPropagation();
              onDismiss();
            }} className="w-12 h-12 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/50 transition-colors cursor-pointer" data-id="element-542">
                    <XIcon className="w-5 h-5" data-id="element-543" />
                  </button>
                </div>

                {/* Tap to dismiss hint */}
                <div className="pointer-events-none absolute top-8 left-1/2 -translate-x-1/2" data-id="element-544">
                  <p className="text-white/40 text-sm" data-id="element-545">
                    Tap anywhere to exit screensaver
                  </p>
                </div>
              </motion.div>}
          </AnimatePresence>
        </motion.div>}
    </AnimatePresence>;
}